import { Link } from 'expo-router';
import { StyleSheet, Text } from 'react-native';

import { useTheme } from '@/hooks/use-theme';

import { useWineTourRows, type WineTourTableProps } from './wine-tour-rows';

import css from './wine-tour-table.module.css';

/**
 * Web: a real <table> in the static HTML — price, duration, format and
 * reviews side by side for "compare wine tours {city}" searches, which a grid
 * of cards doesn't answer. Cell text stays RN <Text> so it shares the site's
 * typography; each title links to the tour page.
 */
export function WineTourTable({ tours, showCity }: WineTourTableProps) {
  const theme = useTheme();
  const { rows, columns } = useWineTourRows(tours);
  if (rows.length === 0) return null;

  const border = { borderBottomColor: theme.border };
  const head = [styles.head, { color: theme.mutedForeground }];
  const cell = [styles.cell, { color: theme.text }];

  return (
    <div className={css.scroller}>
      <table className={css.table}>
        <thead>
          <tr>
            <th scope="col" style={border}>
              <Text style={head}>{columns.tour}</Text>
            </th>
            {showCity ? (
              <th scope="col" style={border}>
                <Text style={head}>{columns.from}</Text>
              </th>
            ) : null}
            <th scope="col" style={border}>
              <Text style={head}>{columns.duration}</Text>
            </th>
            <th scope="col" style={border}>
              <Text style={head}>{columns.format}</Text>
            </th>
            <th scope="col" className={css.number} style={border}>
              <Text style={head}>{columns.rating}</Text>
            </th>
            <th scope="col" className={css.number} style={border}>
              <Text style={head}>{columns.price}</Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <th scope="row" className={css.titleCell} style={border}>
                <Link href={row.href} style={[styles.title, { color: theme.primary }]}>
                  {row.title}
                </Link>
              </th>
              {showCity ? (
                <td className={css.nowrap} style={border}>
                  <Text style={cell}>{row.city}</Text>
                </td>
              ) : null}
              <td className={css.nowrap} style={border}>
                <Text style={cell}>{row.duration}</Text>
              </td>
              <td className={css.nowrap} style={border}>
                <Text style={cell}>{row.format}</Text>
              </td>
              <td className={css.number} style={border}>
                <Text style={cell}>{row.rating}</Text>
              </td>
              <td className={css.number} style={border}>
                <Text style={[cell, styles.price, { color: theme.gold }]}>{row.price}</Text>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const styles = StyleSheet.create({
  head: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  cell: {
    fontSize: 14,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 20,
  },
  price: {
    fontWeight: '700',
  },
});
